<template>
<RouterLink v-if="!disabled" v-bind="mappedProps" @click="onClick">
    <slot />
</RouterLink>
<span v-else v-bind="mappedAttrs"><slot /></span>
</template>
<script setup lang="ts">
import { flow, pickBy } from 'lodash/fp'
import { computed, onBeforeMount, unref, useAttrs, } from 'vue'
import type { RouterLinkProps } from 'vue-router'
import { RouterLink, } from 'vue-router'

type Params = RouterLinkProps & { disabled?: boolean }

const attrs = useAttrs()

const props = withDefaults(
    defineProps<Params>(),
    {
        disabled: false,
    }
)

const mappedProps = computed(() => {
    const { disabled, ..._props } = props

    const attrs = unref(mappedAttrs)
    const _mapped = flow(
        pickBy(value => value !== undefined),
    )(_props)

    const result =  {
        exactActiveClass: "active",
        ...attrs,
        ..._mapped,
    }

    return result as unknown as RouterLinkProps
})

// When disabled, don't include the props on the resulting span
const mappedAttrs = computed(() => {
    const rawClasses = (attrs['class'] ?? '')
    const classes = rawClasses === '' ? [] : (rawClasses as string).split(' ').filter(Boolean)
    if (props.disabled && !classes.includes('disabled')) {
        classes.push('disabled')
    }

    return {
        ...attrs,
        'class': classes.join(' '),
        'aria-disabled': props.disabled ? true : undefined,
    }
})

function onClick (event: Event) {
    if (props.disabled) {
        event.preventDefault()
    }
}

onBeforeMount(() => {
    const { to } = props
    if ((typeof to !== 'string') && !('name' in to)) {
        console.debug('PopupRouterLink', to, props)
    }
})

defineOptions({
    inheritAttrs: false,
})

</script>
<style scoped>
:deep() .router-link.active {
    text-decoration: undeline;
}
</style>
